/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'

import Text from '../../../../components/ts-components/Text'
import { getInviteContext } from '../utils/inviteContext'
import type { InviteSignupInvite } from '../types'

type InviteContextLineProps = {
  invite: InviteSignupInvite
}

/**
 * The line above the form that says who is being invited, to what, and as what.
 *
 * Family invitations read differently enough ("joining X's family as their
 * guardian") that they get their own branch, matching the legacy copy.
 */
const InviteContextLine = ({ invite }: InviteContextLineProps) => {
  const { inviteeName, destination, role, roleArticle } =
    getInviteContext(invite)

  if (invite.isFamily) {
    return (
      <Text
        variant='paragraphMedium'
        weight='regular'
        color={colors.NEUTRAL_50}
      >
        <span css={styles.strong}>{inviteeName}</span> has been invited to join{' '}
        <span css={styles.strong}>{invite.familyPlayerName}&apos;s</span>
        {invite.familyHasRelation ? (
          <>
            {' '}
            family as their{' '}
            <span css={styles.role}>{invite.familyRelation.toLowerCase()}</span>
          </>
        ) : (
          ' family'
        )}
        .
      </Text>
    )
  }

  return (
    <Text variant='paragraphMedium' weight='regular' color={colors.NEUTRAL_50}>
      <span css={styles.strong}>{inviteeName}</span>{' '}
      {t('Web:isInvitedToJoin', {})} {t('common:the', {})}{' '}
      <span css={styles.strong}>{destination}</span>
      {role ? (
        <>
          {' '}
          as {roleArticle} <span css={styles.role}>{role}</span>
        </>
      ) : null}
      .
    </Text>
  )
}

export default InviteContextLine

const styles = {
  strong: css`
    color: ${colors.NEUTRAL_0};
    font-weight: 600;
  `,
  role: css`
    color: ${colors.PRIMARY};
    font-weight: 600;
  `,
}
